// Work kit group-by helpers (W6 Track B) — pure module, no Stimulus side
// effects (lib no-host-alias regime): grouping is client-side over the grid
// engine's loaded rows, so the math lives in a lib file beside announce.js /
// undo.js, never inside an engine (anti-idea 5).
//
//   groupEntries(entries, field, opts) → ordered [{key, label, rowIds, count}]
//
// `entries` are [{id, value}] pairs the CALLER adapts from the engine
// (rowIds() + getCell(rowId, fieldKey)) or from a DOM fallback; `field` is a
// plain camelCase def {key, type, options?: [{key, label, weight}]}.
//
// Ordering contract (W6 spec Track B deliverable 1): group order follows the
// field's option weight (lower sorts first, the Ui::Fields::Option rule), ties
// break by locale collation, fields without options sort purely by collation
// (numeric-aware), and the no-value bucket always sorts LAST.

// Sentinel group key for rows whose group value is missing/blank. A string
// (DOM data attributes are strings); the __ prefix keeps a real option key
// from colliding.
export const NO_VALUE_KEY = "__no_value__"

// Default label for the no-value bucket — callers pass the kit-locale copy.
export const DEFAULT_NO_VALUE_LABEL = "No value"

// Collation: numeric-aware and accent-insensitive so "áep" sorts beside "aep"
// and "10" sorts after "2" (JPD parity for free-text group values).
export function collatorFor(locale) {
  try {
    return new Intl.Collator(locale || undefined, { numeric: true, sensitivity: "base" })
  } catch {
    return new Intl.Collator("en", { numeric: true, sensitivity: "base" })
  }
}

// Normalize one raw cell value into a group key string. Values arrive as the
// engine's canonical field values: option keys for selects, booleans for
// checkbox, strings otherwise; null/undefined/"" collapse into the sentinel.
export function valueKeyFor(value) {
  if (value === null || value === undefined) return NO_VALUE_KEY
  if (typeof value === "string" && value.trim() === "") return NO_VALUE_KEY
  if (typeof value === "boolean") return value ? "true" : "false"
  return String(value)
}

// Human label for one group key: the option's label when the field carries a
// matching option, the raw value otherwise, the no-value copy for the bucket.
export function labelForKey(field, key, noValueLabel = DEFAULT_NO_VALUE_LABEL) {
  if (key === NO_VALUE_KEY) return noValueLabel
  const option = (field.options || []).find((opt) => String(opt.key) === key)
  if (option) return option.label != null ? String(option.label) : key
  return key
}

// Ordered distinct keys: option-backed keys first in option-weight order,
// then free-text keys by collation, and the no-value bucket always LAST. The
// class split (options before non-options) applies globally — a free-text key
// never lands inside another option's weight band.
export function orderedKeys(field, keys, collator) {
  const options = field.options || []
  const weightOf = new Map(
    options.map((opt) => [String(opt.key), Number.isFinite(opt.weight) ? opt.weight : 0]),
  )

  return [...keys].sort((a, b) => {
    if (a === NO_VALUE_KEY) return 1
    if (b === NO_VALUE_KEY) return -1
    const hasA = weightOf.has(a)
    const hasB = weightOf.has(b)
    if (hasA !== hasB) return hasA ? -1 : 1
    const weightDelta = (weightOf.get(a) ?? 0) - (weightOf.get(b) ?? 0)
    if (weightDelta !== 0) return weightDelta
    const labelA = labelForKey(field, a)
    const labelB = labelForKey(field, b)
    return collator.compare(labelA, labelB)
  })
}

// Group [{id, value}] entries into ordered sections. `opts`: {locale,
// hideEmpty, noValueLabel}. Option-backed fields seed a section for EVERY
// option (the board-lane precedent): an option with no rows still renders,
// and hideEmpty drops those empty sections entirely (a regroup that
// materializes rows restores them). Free-text values create their own
// buckets; the no-value bucket only exists when rows actually lack values.
export function groupEntries(entries, field, opts = {}) {
  const collator = opts.collator || collatorFor(opts.locale)
  const noValueLabel = opts.noValueLabel || DEFAULT_NO_VALUE_LABEL

  const buckets = new Map()
  for (const entry of entries) {
    const key = valueKeyFor(entry.value)
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(entry)
  }
  for (const option of field.options || []) {
    const key = String(option.key)
    if (!buckets.has(key)) buckets.set(key, [])
  }

  const groups = orderedKeys(field, buckets.keys(), collator).map((key) => {
    const members = buckets.get(key) || []
    return { key, label: labelForKey(field, key, noValueLabel), rowIds: members.map((m) => m.id) }
  })

  return opts.hideEmpty ? groups.filter((group) => group.rowIds.length > 0) : groups
}
